import { useState, useEffect } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  TextField,
  CircularProgress,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BadgeIcon from "@mui/icons-material/Badge";
import BlockIcon from "@mui/icons-material/Block";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import RepeatIcon from "@mui/icons-material/Repeat";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";

dayjs.extend(utc);

const INPUT_SX = {
  "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 14 },
};

const REASONS = [
  { value: "lunch", label: "🍽 Lunch" },
  { value: "break", label: "☕ Break" },
  { value: "vacation", label: "🏖 Vacation" },
  { value: "sick_day", label: "🤒 Sick Day" },
  { value: "personal", label: "Personal" },
  { value: "other", label: "Other" },
];

const DAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const EMPTY_FORM = {
  mode: "block", // "block" | "override"
  block_type: "one_time", // "one_time" | "recurring"
  barber_user_id: null,
  reason: "other",
  notes: "",
  date: dayjs().format("YYYY-MM-DD"),
  start_time: "09:00",
  end_time: "10:00",
  days_of_week: [], // [] = every day for recurring
  recurring_until: "",
};

export default function BlockDrawer({
  open,
  onClose,
  barbers = [],
  preselectedBarber = null,
  preselectedDate = null,
  preselectedStartTime = null,
  onCreated,
}) {
  const api = useApi();
  const { user } = useAuth();

  const isAdmin = ["admin", "owner"].includes(user?.role);

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [conflictWarning, setConflictWarning] = useState(null); // { count, conflicts[] }

  // Reset when drawer opens
  useEffect(() => {
    if (!open) return;
    setError(null);
    setConflictWarning(null);
    setForm({
      ...EMPTY_FORM,
      barber_user_id:
        preselectedBarber?.user_id || (isAdmin ? null : user?.user_id) || null,
      date: preselectedDate || dayjs().format("YYYY-MM-DD"),
      start_time: preselectedStartTime || "09:00",
      end_time: preselectedStartTime
        ? dayjs(`2000-01-01 ${preselectedStartTime}`)
            .add(1, "hour")
            .format("HH:mm")
        : "10:00",
    });
  }, [open]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const toggleDay = (day) => {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(day)
        ? f.days_of_week.filter((d) => d !== day)
        : [...f.days_of_week, day],
    }));
  };

  const isEveryDay = form.days_of_week.length === 0;

  const isReady = (() => {
    if (!form.barber_user_id && isAdmin) return false;
    if (!form.start_time || !form.end_time) return false;
    if (form.start_time >= form.end_time) return false;

    if (form.mode === "block") {
      if (form.block_type === "one_time" && !form.date) return false;
    } else {
      if (!form.date) return false;
    }
    return true;
  })();

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);

    const barber_user_id = isAdmin ? form.barber_user_id : user?.user_id;

    let responses;
    try {
      if (form.mode === "override") {
        responses = await api("/barbers/date-overrides.php", {
          method: "POST",
          body: JSON.stringify({
            barber_user_id,
            date: form.date,
            start_time: `${form.start_time}:00`,
            end_time: `${form.end_time}:00`,
            notes: form.notes || null,
          }),
        });
      } else if (form.block_type === "one_time") {
        // Convert local date+time to UTC for one-time blocks
        const startsUtc = dayjs(`${form.date}T${form.start_time}:00`)
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
        const endsUtc = dayjs(`${form.date}T${form.end_time}:00`)
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");

        responses = await api("/barbers/blocks.php", {
          method: "POST",
          body: JSON.stringify({
            barber_user_id,
            reason: form.reason,
            notes: form.notes || null,
            is_recurring: false,
            starts_at: startsUtc,
            ends_at: endsUtc,
          }),
        });
      } else {
        // Recurring block — create one record per selected day.
        // "Every day" sends a single record with day_of_week = null.
        const daysToCreate = isEveryDay ? [null] : form.days_of_week;
        responses = await Promise.all(
          daysToCreate.map((day) =>
            api("/barbers/blocks.php", {
              method: "POST",
              body: JSON.stringify({
                barber_user_id,
                reason: form.reason,
                notes: form.notes || null,
                is_recurring: true,
                day_of_week: day,
                start_time: `${form.start_time}:00`,
                end_time: `${form.end_time}:00`,
                recurring_start_date: form.date || null,
                recurring_end_date: form.recurring_until || null,
              }),
            }),
          ),
        );
      }

      // For multi-day recurring, collect all responses to check for conflicts
      // (single calls return one response; Promise.all returns an array)
      const allResponses = Array.isArray(responses) ? responses : [responses];
      const allConflicts = allResponses.flatMap(
        (r) => r?.data?.conflicts || [],
      );

      if (allConflicts.length > 0) {
        setConflictWarning({
          count: allConflicts.length,
          conflicts: allConflicts,
        });
      } else {
        onCreated?.();
        onClose();
      }
    } catch (err) {
      setError(err?.message || "Failed to save. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", sm: 460 },
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Manage Exceptions</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2.5, borderRadius: 1.5, fontSize: 13 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {conflictWarning && (
          <Alert
            severity="warning"
            sx={{ mb: 2.5, borderRadius: 1.5, fontSize: 13 }}
          >
            <strong>Block saved</strong>, but {conflictWarning.count} existing
            booking{conflictWarning.count > 1 ? "s" : ""} overlap
            {conflictWarning.count === 1 ? "s" : ""} with this block. Review and
            cancel them manually if needed.
          </Alert>
        )}

        {/* ── MODE ─────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 0.75,
            mb: 1.5,
          }}
        >
          <BlockIcon sx={{ fontSize: 15, color: "text.secondary" }} />
          <Typography variant="overline" sx={{ color: "text.secondary" }}>
            Type
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, mb: 2.5 }}>
          {[
            {
              value: "block",
              label: "Block time",
              icon: <BlockIcon sx={{ fontSize: 14 }} />,
            },
            {
              value: "override",
              label: "Add availability",
              icon: <EventAvailableIcon sx={{ fontSize: 14 }} />,
            },
          ].map(({ value, label, icon }) => {
            const sel = form.mode === value;
            return (
              <Chip
                key={value}
                icon={icon}
                label={label}
                onClick={() => set("mode", value)}
                sx={{
                  backgroundColor: sel ? "#1a1a1a" : "white",
                  color: sel ? "white" : "#1a1a1a",
                  border: "1px solid",
                  borderColor: sel ? "#1a1a1a" : "#e5e5e5",
                  fontWeight: 500,
                  fontSize: 12,
                  "& .MuiChip-icon": { color: sel ? "white" : "#6b6b6b" },
                  "&:hover": { backgroundColor: sel ? "#000" : "#f5f5f5" },
                }}
              />
            );
          })}
        </Box>

        {/* ── BARBER (admin only) ───────────────────────────── */}
        {isAdmin && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 0.75,
                mb: 1.5,
              }}
            >
              <BadgeIcon sx={{ fontSize: 15, color: "text.secondary" }} />
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Barber
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
              {barbers.map((barber) => {
                const sel = form.barber_user_id === barber.user_id;
                return (
                  <Chip
                    key={barber.user_id}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            backgroundColor: barber.color?.dot || "#9b9b9b",
                          }}
                        />
                        <span>
                          {barber.first_name} {barber.last_name}
                        </span>
                      </Box>
                    }
                    onClick={() => set("barber_user_id", barber.user_id)}
                    sx={{
                      backgroundColor: sel
                        ? barber.color?.bg || "#1a1a1a"
                        : "white",
                      border: `1px solid ${sel ? barber.color?.border || "#1a1a1a" : "#e5e5e5"}`,
                      color: sel ? barber.color?.text || "white" : "#1a1a1a",
                      fontWeight: 500,
                      fontSize: 13,
                      "& .MuiChip-label": { px: 1.25 },
                    }}
                  />
                );
              })}
            </Box>
            <Divider sx={{ mb: 2.5 }} />
          </>
        )}

        {/* ── BLOCK-SPECIFIC: reason + type ────────────────── */}
        {form.mode === "block" && (
          <>
            {/* Block type */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 0.75,
                mb: 1.5,
              }}
            >
              <RepeatIcon sx={{ fontSize: 15, color: "text.secondary" }} />
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Schedule
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, mb: 2.5 }}>
              {[
                { value: "one_time", label: "One-time" },
                { value: "recurring", label: "Recurring" },
              ].map(({ value, label }) => {
                const sel = form.block_type === value;
                return (
                  <Chip
                    key={value}
                    label={label}
                    onClick={() => set("block_type", value)}
                    size="small"
                    sx={{
                      backgroundColor: sel ? "#1a1a1a" : "white",
                      color: sel ? "white" : "#1a1a1a",
                      border: "1px solid",
                      borderColor: sel ? "#1a1a1a" : "#e5e5e5",
                      fontWeight: 500,
                      fontSize: 12,
                      "&:hover": { backgroundColor: sel ? "#000" : "#f5f5f5" },
                    }}
                  />
                );
              })}
            </Box>

            {/* Reason */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 0.75,
                mb: 1.5,
              }}
            >
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Reason
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
              {REASONS.map(({ value, label }) => {
                const sel = form.reason === value;
                return (
                  <Chip
                    key={value}
                    label={label}
                    onClick={() => set("reason", value)}
                    size="small"
                    sx={{
                      backgroundColor: sel ? "#1a1a1a" : "white",
                      color: sel ? "white" : "#1a1a1a",
                      border: "1px solid",
                      borderColor: sel ? "#1a1a1a" : "#e5e5e5",
                      fontWeight: 500,
                      fontSize: 12,
                      "&:hover": { backgroundColor: sel ? "#000" : "#f5f5f5" },
                    }}
                  />
                );
              })}
            </Box>
            <Divider sx={{ mb: 2.5 }} />
          </>
        )}

        {/* ── DATE / DAYS ───────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 0.75,
            mb: 1.5,
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: 15, color: "text.secondary" }} />
          <Typography variant="overline" sx={{ color: "text.secondary" }}>
            {form.mode === "block" && form.block_type === "recurring"
              ? "Days"
              : "Date"}
          </Typography>
        </Box>

        {form.mode === "block" && form.block_type === "recurring" ? (
          <>
            {/* Day of week chips */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
              <Chip
                label="Every day"
                onClick={() => set("days_of_week", [])}
                size="small"
                sx={{
                  backgroundColor: isEveryDay ? "#1a1a1a" : "white",
                  color: isEveryDay ? "white" : "#1a1a1a",
                  border: "1px solid",
                  borderColor: isEveryDay ? "#1a1a1a" : "#e5e5e5",
                  fontWeight: 500,
                  fontSize: 12,
                  "&:hover": {
                    backgroundColor: isEveryDay ? "#000" : "#f5f5f5",
                  },
                }}
              />
              {DAYS.map(({ value, label }) => {
                const sel = form.days_of_week.includes(value);
                return (
                  <Chip
                    key={value}
                    label={label}
                    onClick={() => toggleDay(value)}
                    size="small"
                    sx={{
                      backgroundColor: sel ? "#1a1a1a" : "white",
                      color: sel ? "white" : "#1a1a1a",
                      border: "1px solid",
                      borderColor: sel ? "#1a1a1a" : "#e5e5e5",
                      fontWeight: 500,
                      fontSize: 12,
                      "&:hover": { backgroundColor: sel ? "#000" : "#f5f5f5" },
                    }}
                  />
                );
              })}
            </Box>

            {/* Recurring from / until */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
              <TextField
                label="From (optional)"
                type="date"
                size="small"
                fullWidth
                sx={INPUT_SX}
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
                slotProps={{ htmlInput: { min: dayjs().format("YYYY-MM-DD") } }}
              />
              <TextField
                label="Until (optional)"
                type="date"
                size="small"
                fullWidth
                sx={INPUT_SX}
                value={form.recurring_until}
                onChange={(e) => set("recurring_until", e.target.value)}
                slotProps={{
                  htmlInput: { min: form.date || dayjs().format("YYYY-MM-DD") },
                }}
              />
            </Box>
          </>
        ) : (
          <TextField
            type="date"
            size="small"
            fullWidth
            sx={{ ...INPUT_SX, mb: 2.5 }}
            value={form.date}
            slotProps={{ htmlInput: { min: dayjs().format("YYYY-MM-DD") } }}
            onChange={(e) => set("date", e.target.value)}
          />
        )}

        {/* ── TIME ─────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 0.75,
            mb: 1.5,
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 15, color: "text.secondary" }} />
          <Typography variant="overline" sx={{ color: "text.secondary" }}>
            Time
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
          <TextField
            label="Start time"
            type="time"
            size="small"
            fullWidth
            sx={INPUT_SX}
            value={form.start_time}
            onChange={(e) => set("start_time", e.target.value)}
          />
          <TextField
            label="End time"
            type="time"
            size="small"
            fullWidth
            sx={INPUT_SX}
            value={form.end_time}
            onChange={(e) => set("end_time", e.target.value)}
          />
        </Box>
        {form.start_time >= form.end_time &&
          form.start_time &&
          form.end_time && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 1.5, fontSize: 12 }}
            >
              End time must be after start time.
            </Alert>
          )}

        {/* ── NOTES ────────────────────────────────────────── */}
        <Divider sx={{ mb: 2.5 }} />
        <Typography
          variant="overline"
          sx={{ display: "block", mb: 1.5, color: "text.secondary" }}
        >
          Notes (optional)
        </Typography>
        <TextField
          multiline
          rows={2}
          size="small"
          fullWidth
          sx={INPUT_SX}
          placeholder={
            form.mode === "override"
              ? "e.g. Covering Saturday for the holidays"
              : "e.g. Team lunch — back at 1:30pm"
          }
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
        />
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderTop: "1px solid #f0f0f0",
          backgroundColor: "#fafafa",
          flexShrink: 0,
        }}
      >
        {conflictWarning ? (
          <Button
            onClick={() => {
              onCreated?.();
              onClose();
            }}
            fullWidth
            sx={{
              backgroundColor: "#1a1a1a",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              py: 1.25,
              borderRadius: 1.5,
              "&:hover": { backgroundColor: "#000" },
            }}
          >
            Got it, close
          </Button>
        ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={onClose}
              fullWidth
              sx={{ textTransform: "none", color: "#6b6b6b", fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isReady || submitting}
              fullWidth
              sx={{
                backgroundColor: isReady ? "#9c6a36" : "#e5e5e5",
                color: isReady ? "white" : "#9b9b9b",
                textTransform: "none",
                fontWeight: 600,
                py: 1.25,
                borderRadius: 1.5,
                "&:hover": { backgroundColor: isReady ? "#7d5429" : "#e5e5e5" },
              }}
            >
              {submitting ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={14} sx={{ color: "white" }} />
                  <span>Saving...</span>
                </Box>
              ) : form.mode === "override" ? (
                "Add Availability"
              ) : (
                "Block Time"
              )}
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
